import { useStorage } from '@vueuse/core'
import { useTheme } from 'vuetify'
import { useI18n } from 'vue-i18n'
import { watch } from 'vue'
import { useConfigStore } from '@core/stores/config'
import { cookieRef, namespaceConfig } from '@layouts/stores/config'
import { themeConfig } from '@themeConfig'

const _syncAppRtl = () => {
  try {
    const configStore = useConfigStore()
    const storedLang = cookieRef('language', null)
    
    let locale
    try {
      const i18n = useI18n({ useScope: 'global' })
      locale = i18n?.locale
    } catch (e) {
      console.warn('[initCore] useI18n failed, skipping i18n sync:', e.message)
      return
    }

    if (!locale) return

    // TODO: Handle case where i18n can't read persisted value
    if (locale.value !== storedLang.value && storedLang.value)
      locale.value = storedLang.value

    // watch and change lang attribute of html on language change
    watch(locale, val => {
      // Update lang attribute of html tag
      if (typeof document !== 'undefined')
        document.documentElement.setAttribute('lang', val)

      // Store selected language in cookie
      storedLang.value = val

      // set isAppRtl value based on selected language
      if (themeConfig.app?.i18n?.langConfig && Array.isArray(themeConfig.app.i18n.langConfig) && themeConfig.app.i18n.langConfig.length) {
        themeConfig.app.i18n.langConfig.forEach(lang => {
          if (lang?.i18nLang === val)
            configStore.isAppRTL = lang.isRTL
        })
      }
    }, { immediate: true })
  } catch (e) {
    console.error('[_syncAppRtl] Fatal error:', e)
  }
}

const _handleSkinChanges = () => {
  const { themes } = useTheme()
  const configStore = useConfigStore()

  // Create skin default color so that we can revert back to original (default skin) color when switch to default skin from bordered skin
  if (themes?.value && typeof themes.value === 'object') {
    const themeArray = Object.values(themes.value)
    if (Array.isArray(themeArray) && themeArray.length) {
      themeArray.forEach(t => {
        if (t?.colors) {
          t.colors['skin-default-background'] = t.colors.background
          t.colors['skin-default-surface'] = t.colors.surface
        }
      })
    }
  }
  watch(() => configStore.skin, val => {
    if (themes?.value && typeof themes.value === 'object') {
      const themeArray = Object.values(themes.value)
      if (Array.isArray(themeArray) && themeArray.length) {
        themeArray.forEach(t => {
          if (t?.colors) {
            t.colors.background = t.colors[`skin-${val}-background`]
            t.colors.surface = t.colors[`skin-${val}-surface`]
          }
        })
      }
    }
  }, { immediate: true })
}


/*
    ℹ️ Set current theme's surface color in localStorage

    Why? Because when initial loader is shown (before vue is ready) we need to what's the current theme's surface color.
    We will use color stored in localStorage to set the initial loader's background color.

    With this we will be able to show correct background color for the initial loader even before vue identify the current theme.
  */
const _syncInitialLoaderTheme = () => {
  const vuetifyTheme = useTheme()

  watch(() => useConfigStore().theme, () => {
    // ℹ️ We are not using theme.current.colors.surface because watcher is independent and when this watcher is ran `theme` computed is not updated
    try {
      if (vuetifyTheme?.current?.value?.colors) {
        useStorage(namespaceConfig('initial-loader-bg'), null).value = vuetifyTheme.current.value.colors.surface
        useStorage(namespaceConfig('initial-loader-color'), null).value = vuetifyTheme.current.value.colors.primary
      }
    } catch (e) {
      console.warn('Failed to sync initial loader theme:', e)
    }
  }, { immediate: true })
}

const initCore = () => {
  console.log('[initCore] Starting initialization...')
  
  try {
    console.log('[initCore] Calling _syncInitialLoaderTheme...')
    _syncInitialLoaderTheme()
    console.log('[initCore] _syncInitialLoaderTheme completed')
  } catch (e) {
    console.error('[initCore] _syncInitialLoaderTheme failed:', e.message, e.stack)
  }
  
  try {
    console.log('[initCore] Calling _handleSkinChanges...')
    _handleSkinChanges()
    console.log('[initCore] _handleSkinChanges completed')
  } catch (e) {
    console.error('[initCore] _handleSkinChanges failed:', e.message, e.stack)
  }

  // ℹ️ We don't want to trigger i18n in SK
  if (themeConfig?.app?.i18n?.enable) {
    try {
      console.log('[initCore] Calling _syncAppRtl...')
      _syncAppRtl()
      console.log('[initCore] _syncAppRtl completed')
    } catch (e) {
      console.error('[initCore] _syncAppRtl failed:', e.message, e.stack)
    }
  }
  
  console.log('[initCore] Initialization complete!')
}

export default initCore
